import React, { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { DarkModeState } from "../../Jotai/index";
import "../../style/MagazineIssues/index.scss";
import { fetchAllMagazinesCached } from "../../lib/api";
import CoverThumb from "../../components/magazines/CoverThumb";
import { formatDateUTC } from "../../lib/date";
import { Link, useSearchParams } from "react-router-dom";

// Responsive page size: 8 on desktop, 4 on mobile
const getPageSize = () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 4 : 8);

export default function MagazineIssues() {
  const [darkMode] = useAtom(DarkModeState);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("search") || "";
  const pageSizeParam = parseInt(searchParams.get("pageSize") || "", 10);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(Math.max(1, pageParam));
  const initialPageSize = Number.isFinite(pageSizeParam) && pageSizeParam > 0 ? pageSizeParam : getPageSize();
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [userPageSizeSet, setUserPageSizeSet] = useState(Number.isFinite(pageSizeParam) && pageSizeParam > 0);
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Load magazines
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    fetchAllMagazinesCached()
      .then((d) => {
        if (!alive) return;
        setItems(Array.isArray(d.items) ? d.items : []);
      })
      .catch((e) => alive && setErr(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const { totalPages, paged, filteredCount } = useMemo(() => {
    // Filter items by search term
    const filtered = searchTerm.trim()
      ? items.filter((mag) =>
          mag.title?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : items;

    const filteredCount = filtered.length;
    const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
    const clampedPage = Math.min(Math.max(1, page), totalPages);
    const start = (clampedPage - 1) * pageSize;
    const end = start + pageSize;
    return { totalPages, paged: filtered.slice(start, end), filteredCount };
  }, [items, page, searchTerm, pageSize]);

  useEffect(() => {
    const params = { page: String(page) };
    if (searchTerm.trim()) params.search = searchTerm.trim();
    params.pageSize = String(pageSize);
    setSearchParams(params, { replace: true });
  }, [page, searchTerm, pageSize, setSearchParams]);

  // Update page size on resize; keep user near the same items if size changes
  useEffect(() => {
    if (userPageSizeSet) return; // don't override manual selection
    const onResize = () => {
      const next = getPageSize();
      setPageSize((prev) => {
        if (prev !== next) {
          const firstItemIndex = (page - 1) * prev;
          const newPage = Math.max(1, Math.floor(firstItemIndex / next) + 1);
          setPage(newPage);
        }
        return next;
      });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [page, userPageSizeSet]);

  const onChangePageSize = (e) => {
    const next = parseInt(e.target.value || "", 10);
    if (!Number.isFinite(next) || next <= 0) return;
    setUserPageSizeSet(true);
    setPageSize((prev) => {
      const firstItemIndex = (page - 1) * prev;
      const newPage = Math.max(1, Math.floor(firstItemIndex / next) + 1);
      setPage(newPage);
      return next;
    });
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const onPrev = () => setPage((p) => Math.max(1, p - 1));
  const onNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
  <main className={`${darkMode ? "darkmode " : ""}mag-issues max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12`}>
      <header className="mb-8">
        <h1 className="mag-title text-4xl md:text-5xl font-bold mb-4">
          Magazine Issues
        </h1>
        <p className="mag-subtitle text-lg mb-6">
          Browse all published issues of 22 West Magazine
        </p>

        {/* Search Bar */}
        <div className="max-w-md">
          <div
            className="mag-search-bar flex items-center bg-white dark:bg-slate-800 rounded-lg px-3 py-2.5 shadow-sm focus-within:ring-2"
            style={{ '--tw-ring-color': '#4cc8f4' }}
          >
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-300 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search magazines by title..."
              className="flex-1 bg-transparent border-0 ring-0 focus:ring-0 outline-none appearance-none leading-5 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-300 text-base md:text-sm"
              style={{ fontSize: window.innerWidth <= 768 ? '16px' : undefined }}
              inputMode="search"
              enterKeyHint="search"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="ml-2 h-5 w-5 flex items-center justify-center text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-200 outline-none border-0 bg-transparent p-0 focus:outline-none focus:ring-0"
                aria-label="Clear search"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {filteredCount === 0 ? "No magazines found" : `Found ${filteredCount} ${filteredCount === 1 ? "magazine" : "magazines"}`}
            </p>
          )}
        </div>
      </header>

      {err && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl shadow-sm">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-700 text-sm">Failed to load magazines: {err.message}</p>
          </div>
        </div>
      )}

    {loading ? (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
    {[...Array(pageSize)].map((_, i) => (
            <div key={i} className="animate-pulse">
        <div className="aspect-[17/22] bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {paged.length > 0 ? (
            <>
              <nav className="w-full grid grid-cols-3 items-center gap-2 md:flex md:flex-wrap md:items-center md:justify-center md:gap-4 mb-5 pb-5 border-b border-gray-200 dark:border-gray-700">
                <div className="justify-self-start">
                  {totalPages > 1 && (
                    <button
                      onClick={onPrev}
                      disabled={page <= 1}
                      className="px-3.5 md:px-4 py-1.5 md:py-2 text-sm font-semibold rounded-full border shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                      style={{ backgroundColor: '#4cc8f4', color: '#ffffff', borderColor: '#38bdf8', boxShadow: '0 4px 10px rgba(12, 148, 196, 0.18)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#38bdf8')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4cc8f4')}
                    >
                      ← Previous
                    </button>
                  )}
                </div>
                <div className="justify-self-center">
                  {totalPages > 1 && (
                    <span className="px-3 md:px-3.5 py-1.5 text-sm md:text-base font-semibold rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-300 dark:border-gray-600">
                      Page <span className="font-bold">{page}</span> of <span className="font-bold">{totalPages}</span>
                    </span>
                  )}
                </div>
                <div className="justify-self-end">
                  {totalPages > 1 && (
                    <button
                      onClick={onNext}
                      disabled={page >= totalPages}
                      className="px-3.5 md:px-4 py-1.5 md:py-2 text-sm font-semibold rounded-full border shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                      style={{ backgroundColor: '#4cc8f4', color: '#ffffff', borderColor: '#38bdf8', boxShadow: '0 4px 10px rgba(12, 148, 196, 0.18)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#38bdf8')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4cc8f4')}
                    >
                      Next →
                    </button>
                  )}
                </div>
                {/* Mobile per-page row (visible on mobile, inline on desktop below) */}
                <div className="col-span-3 flex items-center justify-center gap-2 md:hidden">
                  <span className="per-page-label text-sm md:text-base font-medium">Per page</span>
                  <select
                    id="pageSizeTop"
                    aria-label="Magazines per page"
                    title="Magazines per page"
                    value={pageSize}
                    onChange={onChangePageSize}
                    className="min-w-[88px] px-3 py-2 text-sm rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-400 dark:border-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{ '--tw-ring-color': '#4cc8f4' }}
                  >
                    {[4,8,12,16,24].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                {/* Desktop inline per-page (hidden on mobile) */}
                <div className="hidden md:flex items-center gap-2 ml-1 md:ml-2">
                  <span className="per-page-label text-sm md:text-base font-medium">Per page</span>
                  <select
                    id="pageSizeTop_md"
                    aria-label="Magazines per page"
                    title="Magazines per page"
                    value={pageSize}
                    onChange={onChangePageSize}
                    className="min-w-[96px] px-3 py-2 text-sm md:text-base rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-400 dark:border-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{ '--tw-ring-color': '#4cc8f4' }}
                  >
                    {[4,8,12,16,24].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </nav>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
                {paged.map((mag) => (
                  <MagazineCard key={mag.id || mag.slug} mag={mag} />
                ))}
              </div>

              <nav className="w-full grid grid-cols-3 items-center gap-2 md:flex md:flex-wrap md:items-center md:justify-center md:gap-4 pt-7 border-t border-gray-200 dark:border-gray-700">
                <div className="justify-self-start">
                  {totalPages > 1 && (
                    <button
                      onClick={onPrev}
                      disabled={page <= 1}
                      className="px-3.5 md:px-4 py-1.5 md:py-2 text-sm font-semibold rounded-full border shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                      style={{ backgroundColor: '#4cc8f4', color: '#ffffff', borderColor: '#38bdf8', boxShadow: '0 4px 10px rgba(12, 148, 196, 0.18)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#38bdf8')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4cc8f4')}
                    >
                      ← Previous
                    </button>
                  )}
                </div>
                <div className="justify-self-center">
                  {totalPages > 1 && (
                    <span className="px-3 md:px-3.5 py-1.5 text-sm md:text-base font-semibold rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-300 dark:border-gray-600">
                      Page <span className="font-bold">{page}</span> of <span className="font-bold">{totalPages}</span>
                    </span>
                  )}
                </div>
                <div className="justify-self-end">
                  {totalPages > 1 && (
                    <button
                      onClick={onNext}
                      disabled={page >= totalPages}
                      className="px-3.5 md:px-4 py-1.5 md:py-2 text-sm font-semibold rounded-full border shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                      style={{ backgroundColor: '#4cc8f4', color: '#ffffff', borderColor: '#38bdf8', boxShadow: '0 4px 10px rgba(12, 148, 196, 0.18)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#38bdf8')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4cc8f4')}
                    >
                      Next →
                    </button>
                  )}
                </div>
                {/* Mobile per-page row */}
                <div className="col-span-3 flex items-center justify-center gap-2 md:hidden">
                  <span className="per-page-label text-sm md:text-base font-medium">Per page</span>
                  <select
                    id="pageSizeBottom"
                    aria-label="Magazines per page"
                    title="Magazines per page"
                    value={pageSize}
                    onChange={onChangePageSize}
                    className="min-w-[88px] px-3 py-2 text-sm rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-400 dark:border-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{ '--tw-ring-color': '#4cc8f4' }}
                  >
                    {[4,8,12,16,24].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                {/* Desktop inline per-page */}
                <div className="hidden md:flex items-center gap-2 ml-1 md:ml-2">
                  <span className="per-page-label text-sm md:text-base font-medium">Per page</span>
                  <select
                    id="pageSizeBottom_md"
                    aria-label="Magazines per page"
                    title="Magazines per page"
                    value={pageSize}
                    onChange={onChangePageSize}
                    className="min-w-[96px] px-3 py-2 text-sm md:text-base rounded-full border bg-white dark:bg-slate-800 text-[#0e1038] dark:text-white border-gray-400 dark:border-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{ '--tw-ring-color': '#4cc8f4' }}
                  >
                    {[4,8,12,16,24].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </nav>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm ? `No magazines found matching "${searchTerm}"` : "No magazine issues found."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-lg hover:shadow-lg transition-all"
                  style={{ backgroundColor: '#FF9843' }}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
}

function MagazineCard({ mag }) {
  const fileUrl = useMemo(() => {
    if (!mag) return "";
    if (mag.publicUrl?.endsWith(".pdf")) return mag.publicUrl;
    if (mag.publicUrl && mag.r2Key) return `${mag.publicUrl.replace(/\/$/, "")}/${mag.r2Key}`;
    return mag.publicUrl || "";
  }, [mag]);

  const published = mag.publishedAt ? formatDateUTC(mag.publishedAt, "MMM D, YYYY") : "—";
  const to = `/magazine/read/${encodeURIComponent(mag.slug)}`;

  return (
  <article className="group rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 focus-within:shadow-xl focus-within:-translate-y-2 h-full">
      <Link
        to={to}
    className="flex flex-col h-full focus:outline-none focus:ring-4 focus:ring-offset-2 rounded-xl no-underline text-black dark:text-white"
        style={{ '--tw-ring-color': '#191f5e' }}
        aria-label={`Open ${mag.title || "Magazine"} (published ${published})`}
      >
        <div className="relative overflow-hidden bg-gray-100">
          <CoverThumb 
            src={fileUrl} 
            coverKey={mag.coverKey}
            publicUrl={mag.publicUrl}
            className="w-full transform group-hover:scale-105 transition-transform duration-500 will-change-transform backface-hidden" 
            style={{ imageRendering: '-webkit-optimize-contrast' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
  <div className="px-4 md:px-5 pt-2 md:pt-3 pb-4 md:pb-5 flex-1 flex flex-col">
          <h3 className="text-sm md:text-base font-semibold mb-2 line-clamp-2 group-hover:opacity-80 transition-opacity">
            {mag.title || "Untitled Issue"}
          </h3>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mt-auto">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {published}
          </div>
        </div>
      </Link>
    </article>
  );
}
